import React, { useCallback } from 'react';
import ItemBrowser, { GenericItem } from './ItemBrowser';
import { CharacterData } from '../../types/character';
import './SpellBrowser.css';

interface SpellBrowserProps {
  characterId?: string;
  character?: CharacterData | null;
  baseCharacter?: CharacterData | null; // For duplicate detection
  onAddToCharacter?: (itemType: string, itemId: string) => Promise<void>;
  onRemoveFromCharacter?: (itemType: string, itemId: string) => Promise<void>;
}

const SpellBrowser: React.FC<SpellBrowserProps> = ({
  characterId,
  character,
  baseCharacter,
  onAddToCharacter,
  onRemoveFromCharacter,
}) => {
  // Helper function for title case formatting
  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  // Get character's available domains
  const getCharacterDomains = async (characterClass: string): Promise<string[]> => {
    try {
      const { loadAllClasses } = await import('../../utils/classDataLoader');
      const allClasses = await loadAllClasses();
      const classInfo = allClasses.find((cls: any) => cls.name.toLowerCase() === characterClass.toLowerCase());
      return classInfo?.domains?.map((domain: string) => domain.toLowerCase()) || [];
    } catch (error) {
      console.error('Failed to load class data:', error);
      return [];
    }
  };

  // Helper function to get default levels based on character
  const getDefaultLevels = (): number[] => {
    if (character?.level) {
      const level = typeof character.level === 'string' ? parseInt(character.level) : character.level;
      // Return character level + all lower levels
      return Array.from({ length: level }, (_, i) => i + 1);
    }
    // Default to Level 1 if no character
    return [1];
  };

  // Helper function to get spell card display info
  const getCardDisplayInfo = (item: GenericItem): string => {
    const parts: string[] = [];
    
    if (item.domain) {
      parts.push(toTitleCase(item.domain));
    }
    
    if (item.level) {
      parts.push(`Level ${item.level}`);
    }
    
    return parts.length > 0 ? parts.join(' • ') : toTitleCase(item.type);
  };

  // Load domain cards data
  const loadSpells = useCallback(async (): Promise<GenericItem[]> => {
    // Load domain cards
    const domainCardsData = await import('../../data/domain-cards.json');

    // Get character's available domains if character exists
    let characterDomains: string[] = [];
    if (character?.class) {
      // Get primary class domains
      characterDomains = await getCharacterDomains(character.class);

      // Add multiclass domain if it exists
      if (character.multiclass?.domainId) {
        const multiclassDomain = character.multiclass.domainId.toLowerCase();
        if (!characterDomains.includes(multiclassDomain)) {
          characterDomains.push(multiclassDomain);
        }
      }
    }

    // Transform and filter domain cards
    let filteredCards = domainCardsData.domainCards;

    // Filter by character's class domains if character exists
    if (character?.class && characterDomains.length > 0) {
      filteredCards = domainCardsData.domainCards.filter((card: any) => {
        return characterDomains.includes(card.domain.toLowerCase());
      });
    }
    
    // Deduplicate cards based on ID to prevent duplicate entries from JSON
    const uniqueCards = filteredCards.filter((card: any, index: number, arr: any[]) => {
      return arr.findIndex((c: any) => c.id === card.id) === index;
    });

    // Apply level filtering for multiclass domains (half-level rounded up)
    const characterLevel = typeof character?.level === 'string' ? parseInt(character.level) : character?.level || 1;
    const multiclassDomainId = character?.multiclass?.domainId?.toLowerCase();

    const levelFilteredCards = uniqueCards.filter((card: any) => {
      // Check if this card is from the multiclass domain
      const isFromMulticlassDomain = multiclassDomainId && card.domain.toLowerCase() === multiclassDomainId;

      if (isFromMulticlassDomain) {
        // Apply half-level restriction for multiclass domain cards
        const maxLevel = Math.ceil(characterLevel / 2);
        return card.level <= maxLevel;
      }

      // Primary class domain cards: no additional restriction (normal level filter applies)
      return true;
    });

    const transformedSpells: GenericItem[] = levelFilteredCards.map((card: any) => ({
      id: card.id,
      name: toTitleCase(card.name),
      description: card.description,
      category: 'spell',
      type: card.type || 'domain-card',
      tags: card.tags || [],
      domain: card.domain,
      level: card.level,
      requirements: card.requirements,
    }));

    return transformedSpells;
  }, [character?.class, character?.multiclass?.domainId, character?.level]); // Depends on character class, multiclass domain, and level

  // Helper function to get the title for domain cards
  const getDomainCardsTitle = (): string => {
    if (character?.class) {
      if (character.multiclass) {
        return `${character.class} + ${character.multiclass.className} Domain Cards`;
      }
      return `${character.class} Domain Cards`;
    }
    return 'Domain Cards';
  };

  return (
    <ItemBrowser
      loadItems={loadSpells}
      category="spells"
      itemType="spell"
      libraryTitle={getDomainCardsTitle()}
      characterId={characterId}
      character={character}
      baseCharacter={baseCharacter}
      onAddToCharacter={onAddToCharacter}
      onRemoveFromCharacter={onRemoveFromCharacter}
      showTierFilter={false}
      showLevelFilter={true}
      getDefaultLevels={getDefaultLevels}
      getCardDisplayInfo={getCardDisplayInfo}
    />
  );
};

export default SpellBrowser;